import { Autocomplete } from '@app/form_components';
import { useServices } from '@app/hooks';
import {
  CommonIntent,
  DesignIntent,
  DesignIntentConfig,
  DtmfValue,
  LanguageCode,
  Option
} from '@app/types';
import { removeDuplicates } from '@app/utilities';
import {
  Box,
  Button,
  Dropdown,
  InputIcon,
  NewModal,
  Tab,
  TabList,
  TabsContainer,
  TabPanel,
  Switch
} from '@platform/ui-library';
import {
  ReactEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  GenerateUtterances,
  LinkIntentHierarchy,
  OnChangeUtterance,
  UtteranceList
} from '../intent';
import { updateUtterances } from '../shared/update-utterances';
import styles from './IntentDetails.module.scss';
import { useIntentFormHelpers } from './useIntentFormHelpers';
import { ImportFromCsv } from './ImportFromCsv';
import { LinkToLibrary } from './LinkToLibrary';

export enum IntentDetailsMode {
  create,
  edit,
  view
}

export interface IntentDetailsProps {
  primaryLabel?: string;
  /** Intent to modify. */
  intent: DesignIntent;
  /** Mode of the form to display. */
  mode: IntentDetailsMode;
  /** Called with a @see DesignIntent on save or `null` on cancel. */
  onComplete: (intent: DesignIntent | null) => void;
}

export function IntentDetails({
  primaryLabel,
  intent: designIntent,
  mode,
  onComplete
}: IntentDetailsProps) {
  const {
    errorMessageService,
    intentService,
    loadingService
  } = useServices();
  const [intent, setIntent] = useState(designIntent);
  const [isLinkToLibraryOpen, setIsLinkToLibraryOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isUtteranceGenerationOpen, setIsUtteranceGenerationOpen] = useState(false);
  const [selectedCommonIntent, setSelectedCommonIntent] = useState<CommonIntent>();
  const [selectedLanguage, setSelectedLanguage] = useState(LanguageCode.englishUs);
  const [commonIntents, setCommonIntents] = useState<CommonIntent[]>([]);
  const intentConfig = intent.props.config;
  const linkedIntent = intentConfig.commonIntent;

  const { helpers, isValid } = useIntentFormHelpers(
    intent,
    (linkedIntent) ? ['localeData'] : []
  );
  const linkedUtteranceCount = useMemo(() => {
    return commonIntents.find(({ commonId }) => {
      return commonId === linkedIntent?.intentId;
    })?.localeData[LanguageCode.englishUs]?.phrases.length;
  }, [commonIntents, linkedIntent?.intentId]);

  const setIntentConfig = useCallback(
    <K extends keyof (DesignIntentConfig)>(
      changes: Pick<DesignIntentConfig, K>
    ): void => {
      setIntent((currentIntent) => {
        return {
          ...currentIntent,
          props: {
            ...currentIntent.props,
            config: {
              ...currentIntent.props.config,
              ...changes
            }
          }
        };
      });
    },
    [setIntent]
  );

  useEffect(() => {
    let isLoaded = true;
    loadingService.useLoading(async () => {
      const commonIntents = await intentService.getCommonIntents();
      if (isLoaded) {
        setCommonIntents(commonIntents);
      }
    });
    return () => {
      isLoaded = false;
    };
  }, []);

  const isReadonly = mode === IntentDetailsMode.view;
  const selectedLocaleData = intentConfig.localeData?.[selectedLanguage] ?? {
    phrases: [],
    regularExpression: null
  };

  const appLanguagesArray = window.apm.serialized.languages.map((lang) => {
    return lang.language;
  });
  const indexedLanguages = Object.fromEntries(
    appLanguagesArray.map((value, index) => {
      return [`${index}`, value];
    })
  );

  const title = useMemo((): string => {
    switch (mode) {
      case IntentDetailsMode.create:
        return 'Create';
      case IntentDetailsMode.edit:
        return 'Edit';
      case IntentDetailsMode.view:
        return 'View';
      default: {
        const neverMode: never = mode;
        throw new Error(`Mode ${neverMode} does not have a title.`);
      }
    }
  }, [mode]);

  const saveButton = primaryLabel || (mode === IntentDetailsMode.create ? 'Create' : 'Save');

  /** Functions used for Link to Library */
  const onClickAutocompleteItem = (item: CommonIntent): void => {
    setIsLinkToLibraryOpen(true);
    setSelectedCommonIntent(item);
  };

  const onSearchAutocomplete = (
    inputValue: string
  ): Option<CommonIntent>[] => {
    return commonIntents.filter(({ name }) => {
      return new RegExp(inputValue, 'ig').test(name);
    }).map((commonIntent) => ({
      label: commonIntent.name,
      value: commonIntent
    }));
  };

  const onClickCancel = useCallback(
    (): void => {
      setIsLinkToLibraryOpen(false);
    }, []
  );

  const onClickLinkIntent = useCallback((
    commonIntent: CommonIntent | null
  ): void => {
    if (commonIntent) {
      const {
        commonId,
        description,
        name
      } = commonIntent;

      setIntent((currentIntent) => {
        return {
          ...currentIntent,
          name
        };
      });

      setIntentConfig({
        commonIntent: {
          description: description ?? '',
          intentId: commonId,
          name
        }
      });
    } else {
      setIntentConfig({
        commonIntent: null
      });
    }

    setIsLinkToLibraryOpen(false);
  }, [setIntentConfig, setIsLinkToLibraryOpen]);
  /** End - Link to Library */

  const onChangeRegularExpression = useCallback<ReactEventHandler<HTMLInputElement>>(
    (event) => {
      setIntentConfig({
        localeData: {
          ...intent.props.config.localeData,
          [selectedLanguage]: {
            ...selectedLocaleData,
            regularExpression: event.currentTarget.value
          }
        }
      });
    },
    [setIntentConfig, selectedLanguage, selectedLocaleData]
  );

  const onChangeUtterance = useCallback<OnChangeUtterance>(
    (change) => {
      setIntentConfig({
        localeData: {
          ...intent.props.config.localeData,
          [selectedLanguage]: {
            ...selectedLocaleData,
            phrases: removeDuplicates({
              items: updateUtterances<any>(selectedLocaleData.phrases, change),
              key: (phrase) => {
                return phrase.toLowerCase();
              }
            })
          }
        }
      });
    },
    [setIntentConfig, selectedLanguage, selectedLocaleData]
  );

  const onChangeLanguageSelect = (event: string) => {
    const selectedLanguage = indexedLanguages[event];
    setSelectedLanguage(selectedLanguage);
  };

  const {
    dtmfInput,
    inputType
  } = intentConfig;

  const onSubmit = () => {
    if (!isValid) {
      errorMessageService.addError({
        message: 'Please fill all required fields prior to saving.',
        title: 'Missing Required Fields'
      });
      return;
    }

    const sendData = {
      ...intent,
      props: {
        ...intent.props,
        lastLocalChange: window.apm.timestamp
      }
    };

    onComplete(sendData);
  };

  return (
    <NewModal className={styles.newModal} isOpen>
      <Box className={styles.box} theme="noPadding">
        <div className={styles.header}>
          <h2 className={styles.title}>{title} Intent</h2>
          <button
            type="button"
            className={styles.linkToLibraryButton}
            disabled={isReadonly}
            onClick={() => {
              setIsLinkToLibraryOpen(true);
              const commonIntent = commonIntents.find(({ commonId }) => {
                return commonId === linkedIntent?.intentId;
              });
              setSelectedCommonIntent(commonIntent);
            }}
          >
            <i className="fe icon-folder" />
            {intentConfig.commonIntent?.name ?? 'Link to library intent'}
          </button>
          <InputIcon
            type="dropdown"
            className="ui-dropdown"
            options={indexedLanguages}
            onChange={onChangeLanguageSelect as any}
          />
        </div>
        <div className={styles.content}>
          <div className={styles.formContainer}>
            {(!intentConfig.commonIntent) ? (
              <Autocomplete<CommonIntent>
                disabled={isReadonly}
                getOptions={onSearchAutocomplete}
                labels={{
                  isLoading: 'Looking for Library Intents...',
                  notFound:
                    'The library intent you are looking for does not exist.',
                  title: 'Suggested Library Intents'
                }}
                onSearchValueChange={(value) => {
                  setIntent({
                    ...intent,
                    name: value
                  });
                }}
                onValueChange={onClickAutocompleteItem}
                searchValue={intent.name}
                value={null}
                placeholder="Search for an intent or write a new name"
                required
              />
            ) : (
              <InputIcon
                disabled
                value={intent.name}
              />
            )}
            <InputIcon
              label="Intent Description"
              type="textarea"
              name="description"
              onChange={({ target }) => {
                setIntentConfig({
                  description: (target as HTMLInputElement).value
                });
              }}
              placeholder="Describe what this intent is for"
              readOnly={isReadonly}
              className={styles.textarea}
              extraClasses={styles.inputWrapper}
              value={intent.props.config.description || ''}
              rows={5}
            />
            {(!isReadonly) ? (
              <Switch
                label="Droppable"
                type="checkbox"
                name="droppable"
                onChange={({ target }) => {
                  setIntentConfig({
                    droppable: target.checked
                  });
                }}
                disabled={isReadonly}
                checked={intent.props.config.droppable || false}
              />
            ) : (
              <p className={styles.droppableReadOnlyContainer}>
                Droppable
                <br />
                <span>{(intent.props.config.droppable) ? 'True' : 'False'}</span>
              </p>
            )}
            <div className="flex-column">
              <label className="ui-label">Global DTMF</label>
              {(!isReadonly) ? (
                <Dropdown
                  className="ui-dropdown"
                  onChange={(value) => {
                    setIntentConfig({
                      dtmfInput: value as DtmfValue,
                      inputType: (value === DtmfValue.none) ? 'None' : inputType
                    });
                  }}
                  options={Object.fromEntries(
                    Option.fromLegacy<DtmfValue>(
                      window.apm.knowledgeBase.getDTMFOptions(intent)
                    ).map(({ label, value }) => {
                      return [label, value];
                    })
                  )}
                  value={dtmfInput ?? DtmfValue.none}
                />
              ) : (
                <InputIcon readOnly value={dtmfInput ?? DtmfValue.none} />
              )}
            </div>
            {(!dtmfInput || dtmfInput === DtmfValue.none) ? null : (
              <div className="flex-column height-30">
                <label className="ui-label">Limit to Input Type</label>
                {(!isReadonly) ? (
                  <Dropdown
                    className="ui-dropdown"
                    onChange={(value) => {
                      setIntentConfig({ inputType: value });
                    }}
                    options={Object.fromEntries(
                      Option.fromLegacy<string>(
                        window.apm.knowledgeBase.getInputTypes()
                      ).map(({ label, value }) => {
                        return [label, value];
                      })
                    )}
                    value={inputType ?? 'None'}
                  />
                ) : (
                  <InputIcon readOnly value={inputType ?? 'None'} />
                )}
              </div>
            )}
            <InputIcon
              label="Regular Expression"
              type="text"
              name="regularExpression"
              onChange={onChangeRegularExpression}
              readOnly={isReadonly}
              extraClasses={styles.inputWrapper}
              value={selectedLocaleData.regularExpression ?? ''}
              placeholder='\d{3}-\d{2}-\d{4}'
            />
          </div>
          <div className={styles.tabsContainer}>
            <TabsContainer value="utterances" id="utterances-hierarchy">
              <TabList>
                <Tab value="utterances">Utterances</Tab>
                <Tab value="hierarchy">Link Parent/Child</Tab>
              </TabList>
              <TabPanel value="utterances" className={styles.tabPanel}>
                <UtteranceList
                  disabled={isReadonly}
                  utterances={selectedLocaleData.phrases}
                  onChange={onChangeUtterance}
                  onImport={() => setIsImportOpen(true)}
                  onGenUtt={() => setIsUtteranceGenerationOpen(true)}
                  emptyMessage={
                    (linkedIntent) ? (
                      `The selected common intent has ${linkedUtteranceCount} utterances in English not shown here`
                    ) : 'To create a custom intent add at least 3 utterances'
                  }
                />
              </TabPanel>
              <TabPanel value="hierarchy" className={styles.tabPanel}>
                {intent.name ? (
                  <LinkIntentHierarchy
                    disabled={isReadonly}
                    intentConfig={intentConfig}
                    onConfigChange={setIntentConfig}
                  />
                ) : (
                  <p className={styles.message}>
                    In order to link intent hierarchies, please name this custom Intent.
                  </p>
                )}
              </TabPanel>
            </TabsContainer>
          </div>
        </div>
        <div className={styles.footer}>
          {helpers.length && (
            <div className={styles.footerHelper}>
              <h5>Required Fields:</h5>
              <ul>
                {helpers.map((helper, i) => {
                  return (
                    <li key={i} className={styles[helper.type]} title={helper.title}>
                      {helper.message}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          <div className={styles.buttonGroup}>
            <Button
              onClick={() => {
                onComplete(null);
              }}
              theme="outline"
              type="button"
            >
              Cancel
            </Button>
            {(isReadonly) ? null : (
              <Button
                type="button"
                theme="default"
                onClick={onSubmit}
              >
                {saveButton}
              </Button>
            )}
          </div>
        </div>
      </Box>
      {(!(isUtteranceGenerationOpen || isLinkToLibraryOpen)) ? null : (
        <Box className="overlay" />
      )}
      {(!isLinkToLibraryOpen) ? null : (
        <LinkToLibrary
          commonIntents={commonIntents}
          designIntent={intent}
          onClickCancel={onClickCancel}
          onClickLinkIntent={onClickLinkIntent}
          onClickViewIntent={setSelectedCommonIntent}
          selectedCommonIntent={selectedCommonIntent}
        />
      )}
      <ImportFromCsv
        importUtterances={onChangeUtterance}
        isOpen={isImportOpen}
        onClickBack={() => setIsImportOpen(false)}
      />
      <GenerateUtterances
        intent={intent}
        isOpen={isUtteranceGenerationOpen}
        onChangeUtterance={onChangeUtterance}
        onClose={() => setIsUtteranceGenerationOpen(false)}
      />
    </NewModal>
  );
}
